import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import config from '../config';
import SectionReveal from '../components/SectionReveal';
import './ProductDetail.css';

function ProductDetail() {
  const { id } = useParams();
  const product = config.products.find((p) => p.id === id);

  const [quantity, setQuantity] = useState(1);
  const [pincode, setPincode] = useState('');
  const [pincodeValid, setPincodeValid] = useState(null);

  if (!product) {
    return (
      <main className="product-detail-page">
        <div className="container" style={{ padding: '160px 24px 80px', textAlign: 'center' }}>
          <h2>Product not found</h2>
          <Link to="/menu" className="btn btn-primary" style={{ marginTop: '20px' }}>
            Back to Menu
          </Link>
        </div>
      </main>
    );
  }

  const isFreeDelivery = quantity >= config.delivery.freeDeliveryMinBowls;
  const deliveryCharge = isFreeDelivery ? 0 : config.delivery.deliveryCharge;
  const totalPrice = product.monthlyPrice * quantity + deliveryCharge;

  const handleCheckPincode = () => {
    const isValid = config.serviceablePincodes.includes(pincode.trim());
    setPincodeValid(isValid);
  };

  const handleOrder = () => {
    const url = config.getWhatsAppPlanOrderUrl(
      product.name,
      quantity,
      product.monthlyPrice,
      totalPrice,
      deliveryCharge,
      pincode || 'N/A'
    );
    window.open(url, '_blank');
  };

  return (
    <main className="product-detail-page">
      <section className="product-detail-hero">
        <div className="container">
          <Link to="/menu" className="product-detail__back">
            ← Back to Menu
          </Link>
        </div>
      </section>

      <section className="section product-detail-section">
        <div className="container">
          <div className="product-detail">
            {/* Left Side - Image */}
            <SectionReveal animation="slideInLeft">
              <div className="product-detail__image-wrap">
                {product.badge && (
                  <div className="product-detail__badge" style={{ background: product.color }}>
                    {product.badge}
                  </div>
                )}
                <img src={product.image} alt={product.name} className="product-detail__image" />
              </div>
            </SectionReveal>

            {/* Right Side - Details */}
            <SectionReveal animation="slideInRight" delay={0.15}>
              <div className="product-detail__info">
                <div className="product-detail__type-badge" style={{ background: product.color }}>
                  {product.type}
                </div>
                <h1 className="product-detail__name">{product.name}</h1>
                <p className="product-detail__tagline">{product.tagline}</p>

                <div className="product-detail__plan-details">
                  <div className="product-detail__plan-row">
                    <span className="product-detail__plan-label">Monthly Price</span>
                    <span className="product-detail__plan-value product-detail__plan-value--price">
                      ₹{product.monthlyPrice.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="product-detail__plan-row">
                    <span className="product-detail__plan-label">Price per Cup</span>
                    <span className="product-detail__plan-value">₹{product.sellingPricePerCup}</span>
                  </div>
                  <div className="product-detail__plan-row">
                    <span className="product-detail__plan-label">Plan Duration</span>
                    <span className="product-detail__plan-value">{product.planDurationDays} Days (Mon-Sat)</span>
                  </div>
                </div>

                <div className="product-detail__ingredients">
                  <span className="product-detail__ingredients-label">Ingredients:</span>
                  <div className="product-detail__ingredient-tags">
                    {product.ingredients.map((ing, i) => (
                      <span key={i} className="product-detail__ingredient-tag">{ing}</span>
                    ))}
                  </div>
                  <p className="product-detail__toppings">{product.toppings}</p>
                </div>

                <div className="product-detail__best-for">
                  <strong>Best For:</strong> {product.bestFor}
                </div>

                {/* Quantity Selector */}
                <div className="product-detail__quantity-section">
                  <label className="product-detail__qty-label">Subscriptions:</label>
                  <div className="product-detail__quantity-controls">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
                    <span className="product-detail__quantity-value">{quantity}</span>
                    <button onClick={() => setQuantity(quantity + 1)}>+</button>
                  </div>
                </div>

                {/* Delivery Info */}
                <div className="product-detail__delivery-info">
                  {isFreeDelivery ? (
                    <span className="product-detail__free-delivery">✅ Free Delivery (3+ subscriptions)</span>
                  ) : (
                    <span className="product-detail__delivery-charge">
                      🚚 Delivery Charge: ₹{config.delivery.deliveryCharge} (Free on 3+ subscriptions)
                    </span>
                  )}
                </div>

                {/* Total */}
                <div className="product-detail__total-row">
                  <span>Total Amount:</span>
                  <span className="product-detail__total-price">₹{totalPrice.toLocaleString('en-IN')}</span>
                </div>

                {/* Pincode Check */}
                {pincodeValid === null && (
                  <div className="product-detail__pincode-check">
                    <p className="product-detail__pincode-label">Check delivery availability:</p>
                    <div className="product-detail__pincode-row">
                      <input
                        type="text"
                        value={pincode}
                        onChange={(e) => {
                          setPincode(e.target.value.replace(/\D/g, '').slice(0, 6));
                          setPincodeValid(null);
                        }}
                        placeholder="Enter pincode e.g. 380054"
                        className="product-detail__pincode-input"
                        maxLength={6}
                      />
                      <button
                        onClick={handleCheckPincode}
                        className="btn btn-dark product-detail__pincode-btn"
                        disabled={pincode.length !== 6}
                      >
                        Check
                      </button>
                    </div>
                  </div>
                )}

                {pincodeValid === false && (
                  <div className="product-detail__not-serviceable">
                    <p>❌ Currently we don't serve your area.</p>
                    <p className="product-detail__not-serviceable-sub">
                      We deliver within {config.delivery.radiusKm} KM of {config.delivery.baseArea}
                    </p>
                    <button
                      className="product-detail__retry-btn"
                      onClick={() => { setPincode(''); setPincodeValid(null); }}
                    >
                      Try another pincode
                    </button>
                  </div>
                )}

                {pincodeValid === true && (
                  <div className="product-detail__serviceable">
                    <p>✅ Great! We deliver to {pincode}</p>
                  </div>
                )}

                {/* Order Buttons */}
                <div className="product-detail__actions">
                  <button
                    className="btn btn-whatsapp product-detail__order-btn"
                    onClick={handleOrder}
                  >
                    📱 Order on WhatsApp
                  </button>
                </div>
              </div>
            </SectionReveal>
          </div>

          {/* Description Section Below */}
          <SectionReveal delay={0.3}>
            <div className="product-detail__description-section">
              <h2>About This Plan</h2>
              <p>{product.description}</p>
            </div>
          </SectionReveal>
        </div>
      </section>
    </main>
  );
}

export default ProductDetail;
